import React, {useEffect, useReducer} from 'react';
import styled from 'styled-components';
import {Button, ButtonDropdownField, DropdownList, FlexColumn, FlexRow, Label} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {Condition} from '../contracts/report-definition.types';
import {
	ReportTaxonomy,
	fieldByFilterField,
	isValueEditable,
	resolverKindFor,
	ReportTaxonomyField,
} from '../contracts/report-taxonomy.types';
import {formatConditionSummary, SummaryLabels} from './formatConditionSummary';
import {ConditionValuePicker} from './ConditionValuePicker';
import {useValueSource} from './valueSources/useValueSource';
import {ResolverKind, ValueSource} from './valueSources/valueSource.types';

// Kinds backed by a Relay fetch whose candidate labels are not known until the fetch resolves.
// Only these need the on-mount label warm-up below; the static sources (boolean, workflowStatus)
// already resolve synchronously from their fixed candidate list.
const RELAY_BACKED_KINDS: ResolverKind[] = ['project', 'person', 'client', 'label'];

/** Mounts `source.useCandidates` purely to trigger and observe its fetch, without rendering
 * anything itself. This lets a tile warm its own value source's label cache for its pre-set
 * values as soon as it mounts, instead of only warming when the user opens the value picker
 * dropdown (which mounts `ConditionValuePicker`, and hence `useCandidates`, separately). Calls
 * `onSettled` whenever the fetch is not in flight, so the parent can recompute its summary once
 * the cache has fresh data. */
/** The field is a fixed width, but a summary naming several values is not — it has to clip rather than
 * spill past the control's border. `min-width: 0` is what lets it shrink inside the button's flex row;
 * without it the text sets the floor and overflows instead of truncating. The full text stays reachable
 * on hover and through the control's accessible name. */
const TruncatedText = styled.span`
	display: block;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

/** Keeps a long field label on one line at the width of the control it labels. */
const LabelRow = styled.div`
	max-width: 220px;
`;

function ValueLabelWarmer({source, onSettled}: {source: ValueSource; onSettled: () => void}): null {
	const {loading} = source.useCandidates('');
	useEffect(() => {
		if (!loading) {
			onSettled();
		}
	}, [loading, onSettled]);
	return null;
}

export interface ConditionTileProps {
	condition: Condition;
	index: number;
	taxonomy: ReportTaxonomy;
	onChangeValues: (index: number, ids: string[]) => void;
	onRemove: (index: number) => void;
}

/** Resolves which `ValueSource` registry entry backs this condition's picker. Falls back to
 * `'boolean'` both for genuinely boolean fields and for the non-editable case, where the
 * returned source is never opened but `useValueSource` must still be called with some kind
 * (see the hooks-safety note on `ConditionTile`). */
function resolveKind(taxonomy: ReportTaxonomy, field: ReportTaxonomyField | undefined, editable: boolean): ResolverKind {
	if (!editable || !field || field.dataType === 'boolean') {
		return 'boolean';
	}
	return (resolverKindFor(taxonomy, field) as ResolverKind | undefined) ?? 'boolean';
}

/** One filter condition rendered as a labelled dropdown-field tile with a value picker and a
 * delete affordance. `useValueSource` is a plain registry lookup (no hooks inside), so it is
 * safe to call unconditionally here regardless of whether the field is editable; the Relay/async
 * hooks that actually back a picker only run once `ConditionValuePicker` mounts, i.e. once the
 * dropdown is open. */
export function ConditionTile({condition, index, taxonomy, onChangeValues, onRemove}: ConditionTileProps): JSX.Element {
	const {formatMessage} = useIntl();
	const field = fieldByFilterField(taxonomy, condition.field);
	const editable = field ? isValueEditable(taxonomy, field) : false;
	const kind = resolveKind(taxonomy, field, editable);
	const source = useValueSource(kind);
	// Bumped once the label warmer observes the fetch settle, so the summary below is recomputed
	// against the freshly warmed cache even though the cache itself lives outside React state.
	const [, forceSummaryRecompute] = useReducer((count: number) => count + 1, 0);
	const hasPresetValues = condition.values.length > 0;
	const warmLabelsOnMount = editable && hasPresetValues && RELAY_BACKED_KINDS.includes(kind);

	const labels: SummaryLabels = {
		or: formatMessage({id: 'ai_report_builder.conditions.summary_or', defaultMessage: 'or'}),
		selected: n =>
			formatMessage(
				{id: 'ai_report_builder.conditions.summary_selected', defaultMessage: '{count} selected'},
				{count: n}
			),
		yes: formatMessage({id: 'common.yes', defaultMessage: 'Yes'}),
		no: formatMessage({id: 'common.no', defaultMessage: 'No'}),
	};

	const label = field?.label ?? condition.field;
	// Only pass a resolveLabel for editable fields, where `source` is the real id-backed value
	// source. For non-editable fields `source` is the boolean fallback (see `resolveKind` above),
	// whose resolveLabels never resolves the stored value — passing it here would make every value
	// register as unresolved and collapse the summary to a misleading "N selected" instead of
	// showing the stored value directly.
	const summary = formatConditionSummary(condition, taxonomy, {
		resolveLabel: editable ? id => source.resolveLabels([id])[id] : undefined,
		labels,
	});

	return (
		<FlexColumn gap={'xs'} flex={'0 0 auto'} data-cy={`ai-report-condition-tile-${index}`}>
			{warmLabelsOnMount ? <ValueLabelWarmer source={source} onSettled={forceSummaryRecompute} /> : null}
			<LabelRow>
				<Label data-cy={`ai-report-condition-label-${index}`}>
					<TruncatedText title={label}>{label}</TruncatedText>
				</Label>
			</LabelRow>
			<FlexRow alignItems={'center'} gap={'xs'}>
				<DropdownList>
					<DropdownList.Trigger>
						{/* The accessible name normally comes from string children; the summary is wrapped in an
						 * element to truncate it, which would otherwise stringify to '[object Object]', so the
						 * name is supplied explicitly. */}
						<ButtonDropdownField
							width={'220px'}
							disabled={!editable}
							aria-label={summary}
							data-cy={`ai-report-condition-field-${index}`}
						>
							<TruncatedText title={summary}>{summary}</TruncatedText>
						</ButtonDropdownField>
					</DropdownList.Trigger>
					{editable ? (
						// Fixed width instead of `useTriggerWidth`: the trigger is only 220px, but the
						// search field inside `ConditionValuePicker` can't shrink below its own native
						// minimum width, so matching a narrow trigger overflows the popup horizontally.
						<DropdownList.Content offset={4} width={'300px'}>
							<ConditionValuePicker
								source={source}
								selectedIds={condition.values}
								onChange={ids => onChangeValues(index, ids)}
							/>
						</DropdownList.Content>
					) : null}
				</DropdownList>
				<Button
					iconOnly
					emphasis={'low'}
					icon={'trash'}
					onPress={() => onRemove(index)}
					aria-label={formatMessage({id: 'common.remove'})}
					data-cy={`ai-report-condition-delete-${index}`}
				/>
			</FlexRow>
		</FlexColumn>
	);
}
